import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Dialog, DialogContent, DialogActions, DialogTitle,
    TextField, Grid, Paper, FormControl,
    Checkbox, Button, Autocomplete, CircularProgress,
    FormControlLabel, IconButton, Alert, AlertTitle,
    List, ListItem, ListItemText, InputAdornment,
    FormHelperText, Box
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import 'dayjs/locale/en'
import 'dayjs/locale/pt'
import { useTranslation } from 'react-i18next'
import type { Paycode } from 'repository/timekeeper/@types/PaycodeModel'
import { nameValidation, normalizeString } from 'utils/Validations'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CloseIcon from '@mui/icons-material/Close'
import Draggable from 'react-draggable'

export interface PaycodesCombinationSubmitPayload {
    name: string
    description: string
    start_date: string | null
    end_date: string | null
    paycodes: number[]
    enable: boolean
}

interface InitialValues {
    name?: string
    description?: string
    start_date?: string | null
    end_date?: string | null
    paycodes?: Paycode[]
}

interface RowWithName {
    name: string
}

interface Props {
    open: boolean
    onClose: () => void
    onSubmit: (payload: PaycodesCombinationSubmitPayload) => void
    title: string
    saveButtonText: string
    rows: RowWithName[]
    paycodesOptions: Paycode[]
    loadingPaycodes: boolean
    isSaving: boolean
    initialValues: InitialValues
    requiredPermissions: string[]
    descriptionRows?: number
}

const PaperComponent = (props: React.ComponentProps<typeof Paper>) => {
    const nodeRef = useRef<HTMLDivElement | null>(null)
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    )
}

const safePermissions = (): string[] => {
    try {
        const rawPermissions = localStorage.getItem('permissions')
        const permissions = rawPermissions ? JSON.parse(rawPermissions) : []
        return Array.isArray(permissions) ? permissions : []
    } catch {
        return []
    }
}

const toDayjsOrNull = (value?: string | Dayjs | null): Dayjs | null => (value ? dayjs(value) : null)

const normalizePaycodes = (paycodes: Paycode[] = []): number[] => {
    return paycodes
        .map((paycode) => paycode?.paycode_id)
        .filter((id): id is number => id !== undefined && id !== null)
        .sort((a, b) => String(a).localeCompare(String(b)))
}

const PaycodesCombinationFormDialog = ({
    open,
    onClose,
    onSubmit,
    title,
    saveButtonText,
    rows,
    paycodesOptions,
    loadingPaycodes,
    isSaving,
    initialValues,
    requiredPermissions,
    descriptionRows = 2,
}: Props) => {
    const { t, i18n } = useTranslation()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([null, null])
    const [selectedPaycodes, setSelectedPaycodes] = useState<Paycode[]>([])
    const [alert, setAlert] = useState(false)

    const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
    const localDateFormat = language === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'

    const initialState = useMemo(() => {
        const startDate = toDayjsOrNull(initialValues?.start_date)
        const endDate = toDayjsOrNull(initialValues?.end_date)

        return {
            name: initialValues?.name || '',
            description: initialValues?.description || '',
            dates: [startDate, endDate] as [Dayjs | null, Dayjs | null],
            selectedPaycodes: initialValues?.paycodes || [],
        }
    }, [initialValues])

    useEffect(() => {
        if (!open) {
            return
        }

        setName(initialState.name)
        setDescription(initialState.description)
        setDates(initialState.dates)
        setSelectedPaycodes(initialState.selectedPaycodes)
        setAlert(false)
    }, [open, initialState])

    const existName = (candidate: string) => {
        const normalizedInput = normalizeString(candidate)
        const normalizedInitialName = normalizeString(initialState.name)

        if (normalizedInitialName && normalizedInput === normalizedInitialName) {
            return false
        }

        return rows.some((row) => normalizeString(row.name) === normalizedInput)
    }

    const hasDateRange = Boolean(dates[0] && dates[1])

    const disabledSave = () => {
        return name === '' || existName(name) || !hasDateRange || selectedPaycodes.length === 0 || isSaving
    }

    const hasChanges = () => {
        const currentStart = dates[0] ? dates[0].format('YYYY-MM-DD') : null
        const currentEnd = dates[1] ? dates[1].format('YYYY-MM-DD') : null
        const initialStart = initialState.dates[0] ? initialState.dates[0].format('YYYY-MM-DD') : null
        const initialEnd = initialState.dates[1] ? initialState.dates[1].format('YYYY-MM-DD') : null

        return (
            name !== initialState.name ||
            description !== initialState.description ||
            currentStart !== initialStart ||
            currentEnd !== initialEnd ||
            JSON.stringify(normalizePaycodes(selectedPaycodes)) !== JSON.stringify(normalizePaycodes(initialState.selectedPaycodes))
        )
    }

    const requestClose = () => {
        if (hasChanges()) {
            setAlert(true)
            return
        }

        onClose()
    }

    const handleSubmit = () => {
        if (disabledSave()) {
            return
        }

        onSubmit({
            name,
            description,
            start_date: dates[0] ? dates[0].format('YYYY-MM-DD') : null,
            end_date: dates[1] ? dates[1].format('YYYY-MM-DD') : null,
            paycodes: selectedPaycodes.map((paycode) => paycode.paycode_id),
            enable: true,
        })
    }

    const permissions = safePermissions()
    const missingPermissions = requiredPermissions.filter((permission) => !permissions.includes(permission))

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') {
                    return
                }
                requestClose()
            }}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">{title}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={requestClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alert && (
                    <Alert
                        variant='filled'
                        severity='warning'
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlert(false)} color='inherit' size='small' variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={onClose} color='inherit' size='small' variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>

            <DialogContent dividers>
                <Grid container>
                    {missingPermissions.length > 0 && (
                        <Alert severity='warning'>
                            <AlertTitle>{t('general.alerts.missing_permission.title')}</AlertTitle>

                            <List dense>
                                {missingPermissions.map((permission) => (
                                    <ListItem key={permission}>
                                        <ListItemText primary={permission} />
                                    </ListItem>
                                ))}
                            </List>
                        </Alert>
                    )}

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id='input-name'
                            fullWidth
                            label={t('settings.paycodes_combination.dialog.inputs.name')}
                            value={name}
                            autoComplete='off'
                            variant='outlined'
                            onChange={(e) => {
                                const newName = e.target.value
                                if (nameValidation(newName)) {
                                    setName(newName)
                                }
                            }}
                            disabled={isSaving}
                            required
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position='end'>{`${name.length}/100`}</InputAdornment>,
                                },
                            }}
                            error={existName(name)}
                        />
                        <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }}>
                            <span>{existName(name) ? t('settings.paycodes_combination.dialog.inputs.exist_name') : ''}</span>
                        </FormHelperText>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
                            <DateRangePicker
                                localeText={{
                                    start: t('settings.paycodes_combination.dialog.inputs.effective_date_from'),
                                    end: t('settings.paycodes_combination.dialog.inputs.effective_date_until'),
                                }}
                                onChange={(value) => {
                                    const [start, end] = value ?? [null, null]
                                    setDates([toDayjsOrNull(start), toDayjsOrNull(end)])
                                }}
                                value={[toDayjsOrNull(dates[0]), toDayjsOrNull(dates[1])]}
                                format={localDateFormat}
                                disabled={isSaving}
                                slotProps={{
                                    textField: () => ({
                                        required: true,
                                    }),
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <FormControl fullWidth sx={{ mt: 1.5 }}>
                        <Autocomplete
                            multiple
                            id='paycodes'
                            options={paycodesOptions}
                            disableCloseOnSelect
                            value={selectedPaycodes}
                            getOptionLabel={(option) => option.name}
                            onChange={(_event, newValue) => setSelectedPaycodes(newValue)}
                            isOptionEqualToValue={(option, value) => option.paycode_id === value.paycode_id}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...other } = props
                                return (
                                    <li {...other} key={key}>
                                        <Checkbox
                                            icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                                            checkedIcon={<CheckBoxIcon fontSize='small' />}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.name}
                                    </li>
                                )
                            }}
                            disabled={loadingPaycodes || isSaving}
                            slotProps={{ listbox: { maxHeight: 200, overflow: 'auto' } }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('settings.paycodes_combination.dialog.inputs.paycodes')}
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {loadingPaycodes ? <CircularProgress color='inherit' size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            )}
                            loading={loadingPaycodes}
                            noOptionsText={t('settings.paycodes_combination.dialog.inputs.autocomplete.no_options')}
                            loadingText={t('settings.paycodes_combination.dialog.inputs.autocomplete.loading')}
                        />
                        <div>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={paycodesOptions.length > 0 && selectedPaycodes.length === paycodesOptions.length}
                                        disabled={loadingPaycodes || isSaving}
                                        onChange={(event) => {
                                            if (event.target.checked) {
                                                setSelectedPaycodes(paycodesOptions)
                                            } else {
                                                setSelectedPaycodes([])
                                            }
                                        }}
                                    />
                                }
                                label={t('settings.paycodes_combination.dialog.inputs.autocomplete.select_all')}
                            />
                        </div>
                    </FormControl>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1 }}>
                        <TextField
                            id='filled-textarea'
                            placeholder={t('settings.paycodes_combination.dialog.inputs.max_description')}
                            fullWidth
                            label={t('settings.paycodes_combination.dialog.inputs.description')}
                            value={description}
                            autoComplete='off'
                            multiline
                            rows={descriptionRows}
                            slotProps={{
                                htmlInput: { maxLength: 255 },
                                input: {
                                    endAdornment: <InputAdornment position='end'>{`${description.length}/255`}</InputAdornment>,
                                },
                            }}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={isSaving}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button variant='contained' color='error' onClick={requestClose}>
                    {t('general.buttons.close')}
                </Button>
                <Button variant='contained' color='primary' loading={isSaving} disabled={disabledSave()} onClick={handleSubmit}>
                    {saveButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { PaycodesCombinationFormDialog }
